import {
  Box,
  Button,
  Checkbox,
  GRangeFilterPrice,
  HStack,
  Text,
} from '@gazin/ecommerce-ui'
import { useRouter } from 'next/router'
import {
  Dispatch,
  FormEventHandler,
  memo,
  SetStateAction,
  useMemo,
  useState,
} from 'react'
import { Filter, SelectedFiltersByLinx } from 'src/services/linx-api/dtos'
import { FilterProduct } from 'src/templates/SearchAndCategory'
import { Accordion } from '../accordion'
import { getFilteredPrice, minimumAndMaximumPrice } from 'src/util/products'

interface FilterProductMobileProps {
  filter: Filter
  isFilteringMobile?: boolean
  keyFilter: number
  selectedFilterByLinx?: SelectedFiltersByLinx[]
  selectedFilters: Partial<FilterProduct>[]
  setSelectedFilters: Dispatch<SetStateAction<Partial<FilterProduct>[]>>
}

function FilterProductMobile({
  filter,
  isFilteringMobile = false,
  keyFilter,
  selectedFilterByLinx,
  selectedFilters,
  setSelectedFilters,
}: FilterProductMobileProps) {
  const [showMore, setShowMore] = useState(false)

  const route = useRouter()

  function resetScroll() {
    const filterScroll = document.querySelector(`.filter-scroll-${keyFilter}`)

    filterScroll?.scroll(0, 0)
  }

  const treatedFiltersValues = useMemo(() => {
    const MAX_FILTERS = 5
    const fullFilters = filter.values.length
    const filters = filter.values.slice(0, showMore ? fullFilters : MAX_FILTERS)

    const getNormalizedLabel = (label = '') => {
      return label
        .split(' ')
        .join('-')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    }

    return filters.map((item) => {
      const treatedItemLabel = getNormalizedLabel(item?.label)
      const treatedRouteQueryCategory = getNormalizedLabel(
        route.query.categoria?.[0]
      )

      const isSelected =
        treatedItemLabel === treatedRouteQueryCategory ||
        selectedFilters.some(
          ({ id_valor_unidade }) => id_valor_unidade === item.id
        )

      return { ...item, selected: isSelected }
    })
  }, [filter.values, route.query.categoria, selectedFilters, showMore])

  function handleToggleSelectedFiltersMobile(
    id_atributo: number,
    id_valor_unidade: number,
    tipo: string,
    valor_minimo: number | undefined,
    valor_maximo: number | undefined,
    attribute: string,
    label: string
  ) {
    const TYPE_CONTINUOUS = 'continuous'

    const filterData = {
      id_atributo,
      id_valor_unidade,
      tipo,
      valor_minimo,
      valor_maximo,
      attribute,
      label,
    }

    if (!selectedFilters.length) {
      return setSelectedFilters((prevSelectedFilters) => [
        ...prevSelectedFilters,
        filterData,
      ])
    }

    if (tipo !== TYPE_CONTINUOUS) {
      const existingFilterWithAttribute = selectedFilters.findIndex(
        (item) => item.label === label
      )

      const notFoundIndex = existingFilterWithAttribute === -1
      if (notFoundIndex) {
        return setSelectedFilters((prevSelectedFilters) => [
          ...prevSelectedFilters,
          filterData,
        ])
      }

      return setSelectedFilters((prevSelectedFilter) => {
        const prevSelectedFilterCopy = prevSelectedFilter.slice(0)
        prevSelectedFilterCopy.splice(existingFilterWithAttribute, 1)

        return prevSelectedFilterCopy
      })
    }

    const indexFilterWithIdAtributoAndTipoContinuous =
      selectedFilters.findIndex(
        (item) =>
          item?.id_atributo === id_atributo && item?.tipo === TYPE_CONTINUOUS
      )

    const notFoundIndex = indexFilterWithIdAtributoAndTipoContinuous === -1
    if (notFoundIndex) {
      return setSelectedFilters((prevSelectedFilters) => [
        ...prevSelectedFilters,
        filterData,
      ])
    }

    setSelectedFilters((prevSelectedFilters) => {
      const newPriceObject = {
        ...prevSelectedFilters[indexFilterWithIdAtributoAndTipoContinuous],
        valor_minimo,
        valor_maximo,
      }

      prevSelectedFilters.splice(indexFilterWithIdAtributoAndTipoContinuous, 1)
      return [...prevSelectedFilters, newPriceObject]
    })
  }
  const filteredPrice = getFilteredPrice(selectedFilterByLinx)

  return (
    <>
      <Box
        alignItems="flex-start"
        justifyContent="space-between"
        minW="100%"
        h="100%"
        key={keyFilter}
      >
        <Accordion.Item title={filter?.attribute}>
          {filter?.attribute === 'Preço' ? (
            <Box maxW="95%" margin="0 auto">
              <GRangeFilterPrice
                minimumPrice={
                  filteredPrice?.min ||
                  minimumAndMaximumPrice(filter.values).min
                }
                maximumPrice={
                  filteredPrice?.max ||
                  minimumAndMaximumPrice(filter.values).max
                }
                onChange={
                  ((minimum: number, maximum: number) => {
                    filter?.attribute === 'Preço' &&
                      filter?.values?.map((item: any) => {
                        if (isFilteringMobile) {
                          handleToggleSelectedFiltersMobile(
                            filter.id,
                            item?.id ?? item.unityId,
                            filter.type,
                            minimum,
                            maximum,
                            filter.attribute,
                            item?.label ?? ''
                          )
                        }
                      })
                  }) as ((minimum: number, maximum: number) => void) &
                    FormEventHandler<HTMLDivElement>
                }
                css={`
                  p {
                    display: none;
                  }
                `}
              />
            </Box>
          ) : (
            <>
              <Box
                maxH={`${showMore ? '100%' : '7.5rem'}`}
                h="100%"
                overflow={`${showMore ? 'auto' : 'hidden'}`}
                css={`
                  &::-webkit-scrollbar {
                    display: none;
                  }
                `}
                className={`filter-scroll-${keyFilter}`}
              >
                {treatedFiltersValues?.map((item: any) => {
                  return (
                    <HStack
                      alignItems="center"
                      justifyContent="space-between"
                      key={item.id}
                      pl="1.5rem"
                    >
                      <HStack>
                        <Checkbox
                          borderColor="neutral.200"
                          onChange={() => {
                            handleToggleSelectedFiltersMobile(
                              filter.id,
                              item?.id ?? item.unityId,
                              filter.type,
                              item?.min?.value,
                              item?.max?.value,
                              filter.attribute,
                              item?.label
                                ?.normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '') ?? ''
                            )
                          }}
                          isChecked={item.selected}
                        />
                        <Text
                          color="neutral.700"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontFamily="body"
                        >
                          {item?.label}
                        </Text>
                      </HStack>
                    </HStack>
                  )
                })}
              </Box>
              <Button
                variant="ghost"
                color="primary.500"
                fontSize="0.875rem"
                mt="1rem"
                fontWeight="600"
                lineHeight="1.05rem"
                _hover={{ bg: 'transparent' }}
                h="fit-content"
                px={0}
                alignItems="flex-start"
                justifyContent="flex-start"
                display={`${filter?.values?.length > 5 ? 'flex' : 'none'}`}
                onClick={() => {
                  resetScroll()
                  setShowMore(!showMore)
                }}
              >
                {showMore ? 'Ver menos' : 'Ver tudo'}
              </Button>
            </>
          )}
        </Accordion.Item>
      </Box>
    </>
  )
}

export default memo(FilterProductMobile, function (prevProps, nextProps) {
  const attribute = nextProps.filter.attribute
  const prevSelectedFilters = prevProps.selectedFilters
  const nextSelectedFilters = nextProps.selectedFilters

  function getQuantitySelectedFiltersByAttribute(
    selectedFilters: Partial<FilterProduct>[],
    attribute: string
  ) {
    return selectedFilters.filter((item) => item.attribute === attribute).length
  }

  return (
    getQuantitySelectedFiltersByAttribute(prevSelectedFilters, attribute) ===
      getQuantitySelectedFiltersByAttribute(nextSelectedFilters, attribute) &&
    prevProps.isFilteringMobile === nextProps.isFilteringMobile
  )
})
